import React, { useEffect, useState } from 'react';
import RightsBadge from './RightsBadge';
import AudioPlayer from './AudioPlayer';
import { libraryPreview } from './programmeApi';
import { lyricsMessage, usableLyrics } from './rights';

const INSTRUMENTS = ['Piano', 'Strings', 'Organ', 'Harp', 'Guitar'];
const verseText = verse => (typeof verse === 'string' ? verse : verse?.text || '');
const field = 'w-full border border-brand-dark rounded-lg px-3 py-2 text-sm';

const HymnControls = ({ item, piece, onChange }) => {
  const [preview, setPreview] = useState('');
  const [previewError, setPreviewError] = useState('');
  const lyrics = item.lyrics;
  const text = usableLyrics(lyrics);
  const message = lyricsMessage(lyrics);

  useEffect(() => {
    let active = true;
    setPreview('');
    setPreviewError('');
    if (!piece?.id) return () => { active = false; };

    libraryPreview(piece.id, item.instrument || 'Piano')
      .then(result => {
        if (active) setPreview(result?.url || '');
      })
      .catch(error => {
        if (active) setPreviewError(lyricsMessage(error));
      });

    return () => { active = false; };
  }, [piece?.id, item.instrument]);

  const toggleVerse = verseNumber => {
    const current = item.versesChosen || [];
    onChange({
      versesChosen: current.includes(verseNumber)
        ? current.filter(value => value !== verseNumber)
        : [...current, verseNumber].sort((a, b) => a - b),
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <RightsBadge verdict={piece?.lyricsVerdict} />
        {message && <span className="text-xs text-text-muted">{message}</span>}
      </div>
      <label className="block text-xs font-bold uppercase tracking-widest text-accent-navy">
        Instrument
        <select value={item.instrument} onChange={event => onChange({ instrument: event.target.value })} className={`${field} mt-1`}>
          {INSTRUMENTS.map(instrument => <option key={instrument}>{instrument}</option>)}
        </select>
      </label>
      {preview && <AudioPlayer src={preview} title={`${piece?.title || item.title} preview`} instrument={item.instrument} />}
      {previewError && <p role="status" className="text-xs text-text-muted">{previewError}</p>}
      {!text && <p className="text-xs text-text-muted">{message || 'Lyrics are not included in the programme API response.'}</p>}
      {text && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent-navy mb-1">Verses (none ticked = all)</p>
          {text.verses.map((verse, index) => {
            const verseNumber = Number.isInteger(verse?.n) ? verse.n : index;
            return (
              <label key={verseNumber} className="flex gap-2 text-xs mb-1">
                <input type="checkbox" checked={(item.versesChosen || []).includes(verseNumber)} onChange={() => toggleVerse(verseNumber)} />
                <span><b>{verse?.n ?? index + 1}.</b> {verseText(verse).slice(0, 70)}…</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ItemEditor = ({ item, piece, onChange }) => {
  if (!item) return <div className="bg-white border border-brand-dark rounded-2xl p-4 text-sm text-text-muted">Select an item to edit it.</div>;
  return (
    <div className="bg-white border border-brand-dark rounded-2xl p-4 space-y-3">
      <h3 className="font-serif text-lg font-bold text-accent-navy">Edit: {item.type}</h3>
      <input value={item.title} onChange={event => onChange({ title: event.target.value })} className={field} placeholder="Title" />
      {item.type === 'hymn' && <HymnControls item={item} piece={piece} onChange={onChange} />}
      {item.type === 'hymn' && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={item.hostOwnText} onChange={event => onChange({ hostOwnText: event.target.checked, hostTextConfirmed: event.target.checked ? item.hostTextConfirmed : false })} />
          Use the host&apos;s own text
        </label>
      )}
      {item.hostOwnText && (
        <label className="flex items-start gap-2 text-xs">
          <input type="checkbox" checked={item.hostTextConfirmed} onChange={event => onChange({ hostTextConfirmed: event.target.checked })} />
          <span>I confirm I have the right to use these words.</span>
        </label>
      )}
      {(item.type !== 'hymn' || item.hostOwnText) && (
        <div>
          {item.hostOwnText && <p className="text-[11px] text-text-muted mb-1">Shown as &quot;Text supplied by the host&quot;. The host is responsible for having the right to use it.</p>}
          <textarea rows={6} value={item.text} onChange={event => onChange({ text: event.target.value })} className={field} placeholder="Text" />
        </div>
      )}
      <textarea rows={2} value={item.notes} onChange={event => onChange({ notes: event.target.value })} className={field} placeholder="Private notes (not shown to guests)" />
    </div>
  );
};

export default ItemEditor;